import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import type { BugListItem } from '../data/projectTypes'
import { USER_SETTABLE_STATUSES, STATUS_TOOLTIPS } from '../../netlify/functions/lib/bug-statuses'
import StatusBadge from './StatusBadge'
import { useChangeBugStatusMutation } from '../services/projectDataApi'
import './BugStatusMenu.css'

const MENU_MARGIN = 8

// The clickable status pill, shared by the Bugs-tab table and the overview bug table. The pill itself
// is the trigger (a chevron hints it's interactive); clicking it opens a menu of the valid statuses so
// a bug can be re-triaged straight from a table without opening its detail page. Wrapped in
// data-no-navigate so interacting with it never triggers row navigation.
//
// The menu is rendered in a portal with fixed positioning: the overview bug table scrolls horizontally
// (overflow-x: auto), which forces overflow-y to auto and would clip an absolutely-positioned dropdown
// when there aren't enough rows beneath it. Portaling it to the body escapes that clipping entirely.
export default function BugStatusMenu({ bug }: { bug: BugListItem }) {
  const [changeBugStatus] = useChangeBugStatusMutation()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Position the portaled menu under the trigger, clamped to stay within the viewport. Runs in a layout
  // effect (before paint, so there's no flash) and re-runs on scroll/resize while open so the menu keeps
  // tracking the trigger as the table or page scrolls.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    function reposition() {
      const trigger = triggerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const menuWidth = menuRef.current?.offsetWidth ?? 180
      const maxLeft = window.innerWidth - menuWidth - MENU_MARGIN
      const left = Math.max(MENU_MARGIN, Math.min(rect.left, maxLeft))
      setPos({ top: rect.bottom + 4, left })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  function handleStatusChange(newStatus: string) {
    void changeBugStatus({ id: bug.id, status: newStatus })
    setOpen(false)
  }

  return (
    <div
      className="bug-status-menu"
      data-no-navigate
      data-testid={`bug-status-menu-${bug.id}`}
    >
      <button
        type="button"
        ref={triggerRef}
        className="bug-status-menu__trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change status"
        title={STATUS_TOOLTIPS[bug.status] ?? bug.status}
        data-testid={`bug-status-tip-${bug.id}`}
        data-analytics-event="bug-status-toggle"
      >
        <StatusBadge status={bug.status} variant="bug" />
        <ChevronDown className="bug-status-menu__chevron" size={14} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="bug-status-menu__menu"
            role="menu"
            aria-label="Change bug status"
            data-no-navigate
            data-testid={`bug-status-dropdown-${bug.id}`}
            style={{
              position: 'fixed',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <span className="bug-status-menu__menu-label">Change status</span>
            {USER_SETTABLE_STATUSES.map(s => (
              <button
                type="button"
                key={s}
                className={`bug-status-menu__option ${s === bug.status ? 'bug-status-menu__option--active' : ''}`}
                onClick={() => handleStatusChange(s)}
                role="menuitem"
                data-testid={`bug-status-option-${s}-${bug.id}`}
                data-analytics-event={`bug-status-${s}`}
              >
                <StatusBadge status={s} variant="bug" />
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
